import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /** Test identifier for automation (Playwright). Auto-generated from children text if not provided. */
  'data-testid'?: string;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'text-white focus-visible:ring-2 focus-visible:ring-offset-2 hover:opacity-90',
  secondary:
    'bg-surface-tertiary text-gray-900 hover:bg-gray-200 focus-visible:ring-gray-400 dark:bg-surface-dark-tertiary dark:text-gray-100 dark:hover:bg-gray-600',
  outline:
    'border border-border text-gray-700 hover:bg-surface-secondary focus-visible:ring-brand-500 dark:border-border-dark dark:text-gray-200 dark:hover:bg-surface-dark-secondary',
  ghost:
    'text-gray-700 hover:bg-surface-tertiary focus-visible:ring-brand-500 dark:text-gray-200 dark:hover:bg-surface-dark-tertiary',
  danger: 'bg-danger-500 text-white hover:bg-danger-700 focus-visible:ring-danger-500',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-6 py-3 text-base gap-2.5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      children,
      style,
      'data-testid': testId,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const resolvedTestId =
      testId ||
      (typeof children === 'string'
        ? `btn-${children.toLowerCase().replace(/\s+/g, '-')}`
        : undefined);

    return (
      <button
        ref={ref}
        data-testid={resolvedTestId}
        className={cn(
          'inline-flex items-center justify-center rounded-lg font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          variantStyles[variant],
          sizeStyles[size],
          className,
        )}
        style={
          variant === 'primary'
            ? { backgroundColor: 'var(--backoffice-secondary-600)', ...style }
            : style
        }
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={loading}
        {...props}
      >
        {loading && (
          <svg
            className="h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}
        {!loading && leftIcon && <span aria-hidden="true">{leftIcon}</span>}
        {children}
        {!loading && rightIcon && <span aria-hidden="true">{rightIcon}</span>}
      </button>
    );
  },
);

Button.displayName = 'Button';
